import { Link } from "react-router-dom";
import { useQuery } from "@project/ui";
import { Icon, cn } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { UserAvatarImage } from "@apps/dashboard/components/UserAvatarImage";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import {
  fetchFinancialProfile,
  fetchNotificationUnreadCount,
  homeExpenseQueryKeys,
} from "@apps/dashboard/lib/home-expense-api";
import { dashboardPaths } from "@apps/dashboard/lib/routes";

export function AppTopBar(): JSX.Element {
  const { t, locale } = useI18n();
  const { data } = authClient.useSession();
  const displayName = data?.user?.name?.trim() || data?.user?.email || t("dashboardUi.shell.userStubName");
  const userImage =
    data?.user && typeof data.user === "object" && "image" in data.user && typeof data.user.image === "string"
      ? data.user.image
      : null;
  const { activeHouseId, activeHouse } = useActiveHouse();

  const { data: profile } = useQuery({
    queryKey:
      activeHouseId != null
        ? homeExpenseQueryKeys.financialProfile(locale, activeHouseId)
        : ["financial-profile", "off"],
    queryFn: () => {
      if (!activeHouseId) throw new Error("no house");
      return fetchFinancialProfile(locale, activeHouseId);
    },
    enabled: activeHouseId != null,
  });

  const { data: unread } = useQuery({
    queryKey: homeExpenseQueryKeys.notificationUnread(locale),
    queryFn: () => fetchNotificationUnreadCount(locale),
    enabled: Boolean(data?.user),
  });

  return (
    <header
      className={cn(
        "sticky top-0 z-20 flex h-16 w-full items-center justify-between bg-surface px-6 shadow-sm lg:px-10",
        "outline outline-1 outline-outline-variant/15 outline-offset-[-1px]"
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4 lg:gap-6">
        <Link
          to={dashboardPaths.search}
          className="font-body text-body-md flex min-w-0 max-w-full flex-1 items-center gap-2 rounded-xl py-1.5 pl-1 pr-2 text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-on-surface sm:max-w-xs lg:max-w-md"
        >
          <Icon name="Search" className="size-5 shrink-0 text-primary" strokeWidth={1.5} aria-hidden />
          <span className="truncate">{t("dashboardUi.shell.searchPlaceholder")}</span>
        </Link>
        <div className="hidden min-w-0 border-l border-outline-variant/20 pl-4 md:block">
          <p className="truncate font-headline text-xs font-bold text-primary">
            {activeHouse?.name ?? t("dashboardUi.shell.noActiveHouse")}
          </p>
          {profile ? (
            <p className="truncate text-label-md text-on-surface-variant">
              {t("dashboardUi.shell.openingBalanceLine", {
                amount: formatMinorAsCurrency(profile.openingBankBalanceMinor, profile.currency, locale),
              })}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-4 lg:gap-6">
        <Link
          to={dashboardPaths.notificationsInbox}
          className="relative text-on-surface-variant transition-colors hover:text-primary"
          aria-label={t("dashboardUi.pages.notifications")}
        >
          <Icon name="Bell" className="size-6" strokeWidth={1.5} />
          {unread != null && unread.count > 0 ? (
            <span className="absolute -right-1 -top-1 flex min-w-5 justify-center rounded-full bg-tertiary px-1 font-headline text-[10px] font-bold leading-5 text-on-tertiary">
              {unread.count > 99 ? "99+" : unread.count}
            </span>
          ) : null}
        </Link>
        <div className="flex items-center gap-3 border-l border-outline-variant/20 pl-4 lg:pl-6">
          <div className="hidden text-right sm:block">
            <p className="font-headline text-xs font-bold text-primary">{displayName}</p>
            <p className="text-label-md text-on-surface-variant/80">{t("dashboardUi.shell.userStubRole")}</p>
          </div>
          <Link
            to={dashboardPaths.settings}
            className={cn(
              "size-8 shrink-0 overflow-hidden rounded-full bg-primary-container/30 outline outline-1 outline-outline-variant/15",
              "transition-opacity hover:opacity-90"
            )}
            aria-label={t("dashboardUi.nav.accountData")}
          >
            <UserAvatarImage
              rawImage={userImage}
              className="size-full object-cover"
              fallback={
                <span className="flex size-full items-center justify-center text-primary">
                  <Icon name="User" className="size-4" strokeWidth={1.5} aria-hidden />
                </span>
              }
            />
          </Link>
        </div>
      </div>
    </header>
  );
}
